import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem
} from '@mui/material';
import {
  Save,
  Refresh,
  Notifications,
  NotificationsActive,
  NotificationsOff
} from '@mui/icons-material';
import axios from 'axios';

const NotificationSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  // Загрузка настроек уведомлений
  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('http://localhost:5000/api/admin/notification-settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
      setSaveError('Ошибка загрузки настроек уведомлений');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Обновление одной настройки
  const handleSettingChange = (type, field, value) => {
    setSettings(prev => prev.map(setting =>
      setting.type === type
        ? { ...setting, [field]: value }
        : setting
    ));
  };

  // Сохранение всех настроек
  const handleSaveAll = async () => {
    setLoading(true);
    setSaveMessage('');
    setSaveError('');

    try {
      const promises = settings.map(setting =>
        axios.put(
          `http://localhost:5000/api/admin/notification-settings/${setting.type}`,
          {
            enabled: setting.enabled,
            show_in_app: setting.show_in_app,
            send_to_telegram: setting.send_to_telegram,
            priority: setting.priority
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            }
          }
        )
      );

      await Promise.all(promises);

      setSaveMessage('✅ Все настройки успешно сохранены!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setSaveError('Ошибка сохранения настроек');
      setTimeout(() => setSaveError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Группировка настроек по категориям (ВСЕ 27 ТИПОВ)
  const groupedSettings = {
    'Ранги и купоны': ['rank_up', 'coupon_received', 'boost_activated', 'boost_expired'],
    'Стрики и достижения': ['claim_streak_milestone', 'claim_streak_lost'],
    'Рефералы': ['referral_registered', 'referral_deposit', 'referral_daily_reward'],
    'Депозиты и вывод': [
      'deposit_pending', 'deposit_confirmed',
      'withdrawal_requested', 'withdrawal_processing', 'withdrawal_completed', 'withdrawal_rejected'
    ],
    'Турниры и сезоны': [
      'tournament_started', 'tournament_win', 'tournament_prize',
      'season_started', 'season_ended'
    ],
    'Статусы': ['vip_upgraded', 'leader_status_achieved', 'leader_reward'],
    'Системные': ['security_alert', 'maintenance_scheduled', 'maintenance_completed', 'new_feature']
  };

  const getPriorityLabel = (priority) => {
    if (priority >= 5) return 'Высокий';
    if (priority >= 3) return 'Средний';
    return 'Низкий';
  };

  const getPriorityColor = (priority) => {
    if (priority >= 5) return 'error';
    if (priority >= 3) return 'warning';
    return 'info';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsActive sx={{ fontSize: 32, color: '#8a2be2' }} />
          <Typography variant="h4">Настройки уведомлений</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSettings}
            disabled={loading}
          >
            Обновить
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveAll}
            disabled={loading}
            sx={{
              backgroundColor: '#8a2be2',
              '&:hover': { backgroundColor: '#6a1bb2' }
            }}
          >
            Сохранить все
          </Button>
        </Box>
      </Box>

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {loading && settings.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        Object.entries(groupedSettings).map(([category, types]) => (
          <Paper key={category} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#8a2be2' }}>
              {category}
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Тип уведомления</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Описание</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Включено</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>В приложении</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>В Telegram</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Приоритет</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings
                    .filter(s => types.includes(s.type))
                    .map((setting) => (
                      <TableRow key={setting.type} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {setting.enabled ? (
                              <NotificationsActive sx={{ color: '#4caf50', fontSize: 20 }} />
                            ) : (
                              <NotificationsOff sx={{ color: '#999', fontSize: 20 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {setting.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {setting.description || 'Нет описания'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Switch
                            checked={setting.enabled}
                            onChange={(e) => handleSettingChange(setting.type, 'enabled', e.target.checked)}
                            color="success"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Switch
                            checked={setting.show_in_app}
                            onChange={(e) => handleSettingChange(setting.type, 'show_in_app', e.target.checked)}
                            disabled={!setting.enabled}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Switch
                            checked={setting.send_to_telegram}
                            onChange={(e) => handleSettingChange(setting.type, 'send_to_telegram', e.target.checked)}
                            disabled={!setting.enabled}
                            color="info"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <TextField
                            select
                            size="small"
                            value={setting.priority}
                            onChange={(e) => handleSettingChange(setting.type, 'priority', parseInt(e.target.value))}
                            disabled={!setting.enabled}
                            sx={{ width: 120 }}
                          >
                            <MenuItem value={0}>0 - Низкий</MenuItem>
                            <MenuItem value={1}>1 - Низкий</MenuItem>
                            <MenuItem value={2}>2 - Низкий</MenuItem>
                            <MenuItem value={3}>3 - Средний</MenuItem>
                            <MenuItem value={4}>4 - Средний</MenuItem>
                            <MenuItem value={5}>5 - Высокий</MenuItem>
                            <MenuItem value={6}>6 - Высокий</MenuItem>
                            <MenuItem value={7}>7 - Высокий</MenuItem>
                            <MenuItem value={8}>8 - Критичный</MenuItem>
                            <MenuItem value={9}>9 - Критичный</MenuItem>
                            <MenuItem value={10}>10 - Критичный</MenuItem>
                          </TextField>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      <Paper
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.3)',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Notifications sx={{ color: '#6366f1', fontSize: 24 }} />
          <Typography variant="h6" sx={{ color: '#6366f1', fontWeight: 600 }}>
            Информация о настройках
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#8b5cf6', fontWeight: 600, minWidth: '20px' }}>•</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              <strong>Включено</strong> - общий переключатель уведомления. Если выключено, уведомление не будет создаваться вообще.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#8b5cf6', fontWeight: 600, minWidth: '20px' }}>•</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              <strong>В приложении</strong> - показывать ли уведомление в колокольчике в игре.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#8b5cf6', fontWeight: 600, minWidth: '20px' }}>•</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              <strong>В Telegram</strong> - отправлять ли уведомление в Telegram бота.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#8b5cf6', fontWeight: 600, minWidth: '20px' }}>•</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              <strong>Приоритет</strong> - влияет на порядок отображения (большее значение = выше в списке).
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default NotificationSettings;
